import {
  deserify,
  getLocalStorage,
  IStyles,
  Loader,
  NoContentCard,
  PrimaryButton,
  projectFileToPracticalProject,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Divider, Typography, useMediaQuery } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useEffect, useState } from 'react';
import {
  ProjectSelectableChapter,
  ProjectSelectableResource,
  ProjectSelectableTopic,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { Module_ModuleCategoryEnum } from '@protos/content_management/content.db_pb';
import {
  TaskCreationStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';

import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Assign_project,
  HOME,
  HOMEWORK_MANAGE,
} from '../../../routeHandling/RoutesNomenclature';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { EditWarningPopup } from 'apps/teacher/src/components/EditWarningPopup';
import { interactionEvent } from '../../Auth/auth.events';
import { useDownloadContext } from 'apps/teacher/src/app/Context/DownloadContextProviderV2';
import { useGlobalContext } from 'apps/teacher/src/app/Context/GlobalContextProvider';
import { ChapterTopicPopup } from 'apps/teacher/src/components/ChapterTopicPopup';
import { LessonPlanSelectionCard } from '../components/LessonPlanSelectionCard';
import { LessonPlanSelection } from '../components/LessonPlanSelection';
import { ProjectSettings } from '../components/ProjectSettings';
import { IProjectStep, ProjectStepper } from './components/ProjectStepper';
import {
  resetProjectFlow,
  setCreatedAssessmentId,
  setProjectResourceData,
  setProjectSelection,
} from './reducer/projectFlow.Slice';
import {
  getSelectedCountForTopic,
  getSelectedPracticalResourceId,
  IProjectSelection,
  isPracticalResource,
  isResourceSelected,
  sortByChapterOrder,
  toggleResourceSelection,
  toggleTopicSelection,
} from './projectSelection.utils';

const styles: IStyles = {
  root: {
    boxSizing: 'border-box',
    paddingX: { xs: pxToRem(20), md: pxTovW(50), lg: pxTovW(240) },
    display: 'flex',
    flexDirection: 'column',
    // height: { md: '100vh' },
    alignItems: 'center',
    paddingBottom: { xs: pxToRem(80), md: pxToRem(30), lg: pxTovW(40) },
    maxHeight: {
      xs: `calc(100vh - ${pxToRem(60)})`,
      md: `none`,
    },
    overflow: { xs: 'auto' },
  },
  buttonContainer: {
    position: { xs: 'fixed', md: 'unset' },
    top: { xs: '91%' },
    width: { xs: '90%', md: pxTovW(397) },
    height: { xs: pxToRem(50), md: pxTovW(80), lg: pxTovW(60) },
    marginLeft: 'auto',
    zIndex: { xs: 1, md: 0 },
  },
};

export const CreateProject = () => {
  const [openAddLPPopup, setOpenAddLPPopup] = useState(false);
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  /** Set when the subject genuinely has no book mapped - retrying won't help. */
  const [noContentAvailable, setNoContentAvailable] = useState(false);

  const { subject_id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const [editPopupOpen, setEditPopupOpen] = useState(false);
  const [logoClick, setLogoClick] = useState(false);
  const navigate = useNavigate();
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const dispatch = useAppDispatch();
  const teacher_id = getLocalStorage('userId');

  const { LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler } =
    useCommonServiceClientContext();

  const {
    project_resource_data,
    selected_chapter_id,
    selected_resources,
    selected_section,
    selected_class,
    project_name,
    project_instructions,
    teacher_solution,
  } = deserify(useAppSelector((state) => state.projectFlow));

  const committedSelection: IProjectSelection = {
    chapterId: selected_chapter_id,
    resources: selected_resources,
  };
  /** The popup edits a draft; CONFIRM commits it, closing discards it. */
  const [draftSelection, setDraftSelection] =
    useState<IProjectSelection>(committedSelection);

  const selectedChapter = project_resource_data?.chapters?.find(
    (chapter) => chapter.chapterId === selected_chapter_id
  );

  const backButtonClick = async () => {
    setEditPopupOpen(true);
  };
  const handleLogoClick = () => {
    setLogoClick(true);
    setEditPopupOpen(true);
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const home = searchParams.get('home');
  useEffect(() => {
    setSelectedFunction(() => backButtonClick);
    setSelectedLogoClickHandler(() => handleLogoClick);
    return () => {
      setSelectedFunction(() => null);
      setSelectedLogoClickHandler(() => null);
    };
  }, []);

  /**
   * The project picker is lesson-plan driven, so it is sourced from
   * getProjectResourcesByChapter - the same set creation validates against.
   * One call returns every chapter with its topics and resources.
   */
  const fetchProjectResources = async () => {
    if (!subject_id || !selected_section?.id) {
      return;
    }
    try {
      setLoading('loading');
      setNoContentAvailable(false);
      const response =
        await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.getProjectResourcesByChapter(
          {
            subjectId: Number(subject_id),
            schoolClassSectionId: selected_section.id,
          }
        );
      dispatch(setProjectResourceData(response?.data));
      setLoading('completed');
    } catch (error: any) {
      // not_found means the subject has no book mapped for the year, not a
      // transient failure - surface it as an empty state instead of an error.
      if (error?.code === 'not_found' || error?.code === 5) {
        dispatch(setProjectResourceData(undefined));
        setNoContentAvailable(true);
        setLoading('completed');
        return;
      }
      dispatch(
        setToastInfo({
          label: 'Error fetching project resources',
          variant: 'error',
          open: true,
        })
      );
      setLoading('error');
    }
  };

  useEffect(() => {
    fetchProjectResources();
  }, [subject_id, selected_section?.id]);

  const topicToggleHandler = (
    chapter: ProjectSelectableChapter,
    topic: ProjectSelectableTopic
  ) => {
    setDraftSelection((current) =>
      toggleTopicSelection(current, chapter, topic)
    );
  };

  const resourceToggleHandler = (
    chapter: ProjectSelectableChapter,
    topic: ProjectSelectableTopic,
    resource: ProjectSelectableResource
  ) => {
    setDraftSelection((current) =>
      toggleResourceSelection(current, chapter, topic, resource)
    );
  };

  /**
   * Creates the project, then hands the new assessmentId to the assign screen.
   *
   * taskType TASK_PROJECT_CUSTOM is what puts the request on the project code
   * path server-side; category is always chapter-level; selectedTasksInfo must
   * stay empty because a project has no questions.
   */
  const handleCreateProject = async () => {
    if (!teacher_id || !selectedChapter || !selected_section?.id) return;

    // A project carries exactly one practical, and only that resource may carry
    // the teacher's solution - `practical` on any other type is rejected.
    const practicalResourceId = getSelectedPracticalResourceId(
      selectedChapter,
      selected_resources
    );
    // Never drop an authored solution silently - the teacher would believe it
    // was saved. Stop here; Settings offers "add a practical" or "discard".
    if (teacher_solution && !practicalResourceId) {
      dispatch(
        setToastInfo({
          label:
            "The selected resources can't hold your saved reference solution. Discard it in Settings, then click CREATE PROJECT.",
          variant: 'error',
          open: true,
        })
      );
      return;
    }

    try {
      setLoading('loading');
      const response =
        await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
          {
            teacherId: BigInt(teacher_id),
            subjectId: Number(subject_id),
            sectionId: selected_section.id,
            classId: selected_class?.id,
            taskName: project_name,
            taskType: TaskEnum.TASK_PROJECT_CUSTOM,
            creationStatus:
              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED,
            category: Module_ModuleCategoryEnum.MODULE_CATEGORY_CHAPTER,
            // maxMarks: DEFAULT_PROJECT_MAX_MARKS,
            instructions: project_instructions,
            // Exactly one chapter - the picker cannot produce more.
            chapterIdsInfo: [
              {
                chapterId: selectedChapter.chapterId,
                name: selectedChapter.chapterName ?? '',
                chapterNo: selectedChapter.chapterNo ?? 0,
                subjectId: Number(subject_id),
              },
            ],
            selectedTasksInfo: [],
            // The API's chapter order is the order the student progresses
            // through them. Re-sorted here too because the selection is
            // persisted, so it can predate the current chapter data.
            selectedResourcesInfo: sortByChapterOrder(
              selectedChapter,
              selected_resources
            ).map((resource, index) => ({
              resourceId: resource.resourceId,
              lessonId: resource.lessonId,
              moduleId: resource.moduleId,
              sequence: index + 1,
              // Left off entirely when the teacher authored nothing - an
              // absent metaData keeps whatever is stored, an empty one clears
              // it, and neither should happen by accident.
              ...(teacher_solution &&
                resource.resourceId === practicalResourceId
                ? {
                  metaData: {
                    info: {
                      case: 'practical' as const,
                      value: {
                        teacherSolution:
                          projectFileToPracticalProject(teacher_solution),
                      },
                    },
                  },
                }
                : {}),
            })),
          }
        );

      const assessmentId = response?.data?.assessment?.assessmentId;
      if (response?.status !== 200 || !assessmentId) {
        throw new Error(response?.message || 'Project creation failed');
      }

      dispatch(setCreatedAssessmentId(assessmentId));
      setLoading('completed');
      navigate(Assign_project);
    } catch (error: any) {
      setLoading('completed');
      // failed_precondition messages are written for teachers - show them as-is.
      dispatch(
        setToastInfo({
          label: error?.message
            ? error.message.replace(/.*\]\s*/, '').trim()
            : 'Project creation failed',
          variant: 'error',
          open: true,
        })
      );
    }
  };

  const hasChapters = (project_resource_data?.chapters ?? []).length > 0;
  const hasSelection = !!selectedChapter && selected_resources.length > 0;

  const steps: IProjectStep[] = [
    {
      index: 0,
      stepTitle: 'Resource Selection',
      stepClickHandler: () => setSelectedStepIndex(0),
      stepButtonText: 'NEXT',
      disableStep: !hasSelection,
      stepIconColor: '#007CDC',
      disabledIconColor: '#7EC6FD',
    },
    {
      index: 1,
      stepTitle: 'Settings',
      stepClickHandler: () => setSelectedStepIndex(1),
      stepButtonText: 'CREATE PROJECT',
      disableStep: !hasSelection,
      stepIconColor: '#0AA34F',
      disabledIconColor: '#A9EBC9',
    },
  ];

  const handleNextClick = async () => {
    if (selectedStepIndex === steps.length - 1) {
      await handleCreateProject();
      return;
    }
    setSelectedStepIndex(selectedStepIndex + 1);
  };

  return loading === 'loading' ? (
    <Loader />
  ) : (
    <Box sx={styles.root}>
      <Box
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          marginY: { xs: pxToRem(23), md: pxToRem(31), lg: pxTovW(41) },
          display: 'flex',
        }}
      >
        <Typography variant="h1" fontWeight={600}>
          Project Assessment
        </Typography>
        {hasSelection && (
          <PrimaryButton
            sx={styles.buttonContainer}
            onClick={handleNextClick}
            disabled={steps[selectedStepIndex]?.disableStep}
          >
            <Typography
              variant={isMobile ? 'h2' : 'h3'}
              color={'common.white'}
              fontWeight={'bold'}
            >
              {steps[selectedStepIndex]?.stepButtonText || 'NEXT'}
            </Typography>
          </PrimaryButton>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: { xs: pxToRem(30), md: pxToRem(36), lg: pxTovW(50) },
        }}
      >
        <Box sx={{ width: { xs: pxToRem(166), md: pxTovW(299) } }}>
          <ProjectStepper currentStep={selectedStepIndex} steps={steps} />
        </Box>
      </Box>

      {selectedStepIndex === 1 ? (
        <ProjectSettings
          hasPracticalResource={
            !!getSelectedPracticalResourceId(
              selectedChapter,
              selected_resources
            )
          }
        />
      ) : !hasSelection ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: pxToRem(18), lg: pxTovW(20) },
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              marginY: { xs: pxToRem(23), md: pxToRem(31), lg: pxTovW(41) },
            }}
          >
            <Typography
              variant="h2"
              fontSize={{ xs: pxToRem(18), md: pxToRem(27), lg: pxTovW(35) }}
              fontWeight={600}
            >
              Select Resources to create the Project
            </Typography>
          </Box>
          {hasChapters ? (
            <PrimaryButton
              sx={{
                width: { xs: pxToRem(211), md: pxTovW(397) },
                height: { xs: pxToRem(48), md: pxToRem(53), lg: pxTovW(77) },
                gap: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
              }}
              onClick={async () => {
                setDraftSelection(committedSelection);
                setOpenAddLPPopup(true);
              }}
            >
              <AddCircleIcon
                sx={{
                  color: '#03612D',
                  fontSize: {
                    xs: pxToRem(24),
                    md: pxToRem(31),
                    lg: pxTovW(41),
                  },
                }}
              />
              <Typography
                variant="h2"
                fontWeight="bold"
                color="white"
                sx={{
                  fontSize: { xs: pxToRem(18), md: pxTovW(30), lg: pxTovW(24) },
                }}
              >
                Add Chapter
              </Typography>
            </PrimaryButton>
          ) : (
            <NoContentCard
              variant="info"
              icon="cards"
              text={
                !selected_section?.id
                  ? 'Select a class and section to add chapters'
                  : noContentAvailable
                    ? 'No content mapped for this subject'
                    : 'No Chapters Available'
              }
              rootStyle={{
                mt: { xs: pxToRem(20), md: pxToRem(36), lg: pxTovW(50) },
              }}
            />
          )}
        </Box>
      ) : (
        <LessonPlanSelection
          selectedChapter={selectedChapter}
          selectedResources={selected_resources}
          addMoreHandler={async () => {
            setDraftSelection(committedSelection);
            setOpenAddLPPopup(true);
            await interactionEvent({
              url: 'Teacher_formative_assessment_create',
              context: 'Your_added_chapters',
              name: 'ADD_MORE',
              pathSegments: pathSegments,
              isOffline: isOffline,
            });
          }}
        />
      )}
      {
        <ChapterTopicPopup
          open={openAddLPPopup}
          handleClose={() => {
            setDraftSelection(committedSelection);
            setOpenAddLPPopup(false);
          }}
          popupBody={
            <ChapterListComponent
              chapters={project_resource_data?.chapters ?? []}
              selection={draftSelection}
              topicToggleHandler={topicToggleHandler}
              resourceToggleHandler={resourceToggleHandler}
              confirmHandler={() => {
                dispatch(setProjectSelection(draftSelection));
                setOpenAddLPPopup(false);
              }}
            />
          }
        />
      }
      <EditWarningPopup
        open={editPopupOpen}
        handleClose={() => {
          if (logoClick) {
            setLogoClick(false);
            setSelectedLogoClickHandler(() => handleLogoClick);
          } else {
            setSelectedFunction(() => backButtonClick);
          }
          setEditPopupOpen(false);
        }}
        yesClickHandler={async () => {
          if (logoClick || home) {
            dispatch(resetProjectFlow());
            navigate(HOME);
            return;
          }
          navigate(HOMEWORK_MANAGE);
          await interactionEvent({
            url: 'Teacher_formative_assessment_create',
            context: 'chapter_delete',
            name: 'YES',
            pathSegments: pathSegments,
            isOffline: isOffline,
          });
        }}
        noClickHandler={async () => {
          if (logoClick) {
            setLogoClick(false);
            setSelectedLogoClickHandler(() => handleLogoClick);
          } else {
            setSelectedFunction(() => backButtonClick);
          }
          setEditPopupOpen(false);
          await interactionEvent({
            url: 'Teacher_formative_assessment_create',
            context: 'chapter_delete',
            name: 'NO',
            pathSegments: pathSegments,
            isOffline: isOffline,
          });
        }}
        iconname="back"
        text1="If you go back, you may lose your changes."
        text2="Are you sure you want go back?"
      />
    </Box>
  );
};

interface IPopupBodyProps {
  chapters: ProjectSelectableChapter[];
  selection: IProjectSelection;
  confirmHandler: () => void;
  topicToggleHandler: (
    chapter: ProjectSelectableChapter,
    topic: ProjectSelectableTopic
  ) => void;
  resourceToggleHandler: (
    chapter: ProjectSelectableChapter,
    topic: ProjectSelectableTopic,
    resource: ProjectSelectableResource
  ) => void;
}
export const ChapterListComponent = (props: IPopupBodyProps) => {
  const {
    chapters,
    selection,
    confirmHandler,
    topicToggleHandler,
    resourceToggleHandler,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          height: 'max-content',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: pxToRem(16), md: pxToRem(21), lg: pxTovW(24) },
            }}
          >
            Add Chapter
          </Typography>
          <Typography variant="smallText" color="secondary.main">
            {selection.resources.length
              ? `${selection.resources.length} resources selected`
              : ''}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: pxToRem(10), md: pxToRem(21), lg: pxTovW(25) },
          }}
        >
          <PrimaryButton
            onClick={confirmHandler}
            sx={{
              width: { xs: pxToRem(84), md: pxTovW(183), lg: pxTovW(153) },
              height: { xs: pxToRem(28), md: pxTovW(63), lg: pxTovW(53) },
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            <Typography
              variant={isMobile ? 'h5' : 'h5'}
              color="common.white"
              fontWeight={700}
              sx={{ fontSize: { xs: pxToRem(12), md: 'inherit' } }}
            >
              CONFIRM
            </Typography>
          </PrimaryButton>
        </Box>
      </Box>
      <Divider
        sx={{
          width: '100%',
          height: '1px solid #E0DFDE',
          marginY: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
        }}
      />
      <Box
        sx={{
          width: { xs: '100%', md: '100%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: pxToRem(18), lg: pxTovW(20) },
          height: '90%',
          overflowY: 'auto',
        }}
      >
        {chapters.flatMap((chapter) => {
          const isChapterSelection = selection.chapterId === chapter.chapterId;
          return (chapter.topics ?? []).map((topic) => (
            <Box key={topic.topicId} sx={{ width: '100%' }}>
              <LessonPlanSelectionCard
                title={topic.topicName}
                subtitle={chapter.chapterName}
                posterImageUrl={topic.posterImageUrl}
                totalResourceCount={(topic.resources ?? []).length}
                selectedCount={getSelectedCountForTopic(
                  selection,
                  chapter,
                  topic
                )}
                resources={topic.resources ?? []}
                isResourceChecked={(resource) =>
                  isChapterSelection &&
                  isResourceSelected(selection.resources, resource.resourceId)
                }
                isResourceDisabled={(resource) =>
                  isChapterSelection && isPracticalResource(resource)
                }
                toggleHandler={() => topicToggleHandler(chapter, topic)}
                resourceToggleHandler={(resource) =>
                  resourceToggleHandler(chapter, topic, resource)
                }
                variant="medium"
              />
            </Box>
          ));
        })}
      </Box>
    </>
  );
};
